import { Formik, Form } from "formik";
import { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { PhoneInput } from "@/core/components/phone-input";
import { SignupSourceSelector } from "@/core/components/signup-source-selector";
import { Button } from "@volca/ui/components/ui/button";
import { FormikCheckboxWithLabel } from "@volca/ui/components/ui/checkbox-with-label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@volca/ui/components/ui/dialog";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { useBusiness } from "@/core/business";
import { normalizeSignupSource } from "@/core/utils/form";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { ICustomer } from "@/customer/customer";
import { useUpsertCustomer } from "@/customer/query";
import { useGetMyUser } from "@/user/query";

const initialValues = {
  name: "",
  phone_number: "",
  signup_source_id: "none",
  signup_source_type: "",
  onboard: false,
};

interface CustomerFormValues {
  name: string;
  phone_number: string;
  signup_source_id: string;
  signup_source_type: string;
  onboard: boolean;
}

interface AddAdvocateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (data: ICustomer) => void;
  onError?: (error: Error) => void;
  trigger?: React.ReactElement;
  businessId?: string;
}

const advocateSchema = Yup.object().shape({
  name: Yup.string().required("Customer name is required"),
  phone_number: Yup.string()
    .required("Phone number is required")
    .matches(
      /^\(\d{3}\) \d{3}-\d{4}$/,
      "Please enter a valid phone number: (XXX) XXX-XXXX",
    )
    .test("is-complete", "Please enter a complete phone number", (value) => {
      if (!value) return false;
      const digitCount = value.replace(/\D/g, "").length;
      return digitCount === 10;
    }),
  signup_source_id: Yup.string(),
  signup_source_type: Yup.string(),
  onboard: Yup.boolean(),
});

const AddAdvocateModal = ({
  open,
  onOpenChange,
  onSuccess,
  onError,
  trigger,
  businessId,
}: AddAdvocateModalProps) => {
  const { businessId: userBusinessId } = useBusiness();

  const effectiveBusinessId = businessId || (userBusinessId ?? undefined);

  const { mutate: upsertCustomer } = useUpsertCustomer(effectiveBusinessId);
  const { data: currentUser } = useGetMyUser();

  const memoizedInitialValues = useMemo(
    () => ({
      ...initialValues,
      signup_source_id: currentUser?.id || "none",
      signup_source_type: "User",
    }),
    [currentUser?.id],
  );

  const handleSubmit = useCallback(
    async (values: CustomerFormValues) => {
      // Ensure both signup source fields are set or neither is set
      const normalizedSignupSource = normalizeSignupSource(
        values.signup_source_id,
        values.signup_source_type,
      );

      return upsertCustomer(
        {
          name: values.name,
          phone_number: values.phone_number,
          ...normalizedSignupSource,
          status: CUSTOMER_STATUS.ACTIVE,
          onboard: values.onboard,
        },
        {
          onSuccess: (data: ICustomer) => {
            toast.success("Advocate created successfully");
            onSuccess?.(data);
            onOpenChange(false);
          },
          onError: (error) => {
            toast.error("Failed to create advocate", {
              description: error.message,
            });
            onError?.(error);
          },
        },
      );
    },
    [upsertCustomer, onSuccess, onError, onOpenChange],
  );

  return (
    <Formik
      initialValues={memoizedInitialValues}
      validationSchema={advocateSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
        resetForm();
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      {({ values, getFieldProps, setFieldValue, isSubmitting, isValid }) => (
        <Dialog open={open} onOpenChange={onOpenChange}>
          {trigger && <DialogTrigger render={trigger} />}
          <DialogContent className="max-h-[80vh] overflow-y-auto overscroll-contain">
            <Form className="grid gap-4">
              <DialogHeader>
                <DialogTitle>New advocate</DialogTitle>
                <DialogDescription className="sr-only">
                  Create a new advocate for your business.
                </DialogDescription>
              </DialogHeader>

              <Stack align="start" className="w-full" gap={6}>
                <FormInput
                  label="Customer name"
                  {...getFieldProps("name")}
                  fullWidth
                />
                <PhoneInput
                  value={values.phone_number}
                  onChange={(value) => setFieldValue("phone_number", value)}
                  label="Phone number"
                  fullWidth
                  required
                  name="phone_number"
                />
                <SignupSourceSelector
                  id="signup-source-add-advocate"
                  businessId={effectiveBusinessId}
                  selectedSourceId={values.signup_source_id}
                  selectedSourceType={values.signup_source_type}
                  onSourceChange={(sourceId, sourceType) => {
                    setFieldValue("signup_source_id", sourceId);
                    setFieldValue("signup_source_type", sourceType);
                  }}
                />
                <FormikCheckboxWithLabel
                  id="onboard"
                  label="Send advocate program sign-up SMS"
                  checked={values.onboard}
                  onCheckedChange={(checked) =>
                    setFieldValue("onboard", !!checked)
                  }
                />
              </Stack>

              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                >
                  Create
                </Button>
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      )}
    </Formik>
  );
};

export default AddAdvocateModal;
